import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { backendUrl } from '../utils/backendUrl';
import UserTimezoneSelect from './UserTimezoneSelect';
import PasswordHint from './PasswordHint';
import './Login.css'; // Reusing the same styles

const Signup = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        organization: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError(''); // Clear error when user starts typing
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Client-side validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (formData.password.length < 15) {
            setError('Password must be at least 15 characters');
            setLoading(false);
            return;
        }

        if (!formData.email.trim() || !formData.email.includes('@')) {
            setError('A valid email is required');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(backendUrl('/auth/signup'), {
                username: formData.username,
                email: formData.email.trim(),
                password: formData.password,
                confirmPassword: formData.confirmPassword,
                organization: formData.organization.trim(),
                workspaceName: formData.organization.trim()
            }, {
                withCredentials: true,
                timeout: 20000
            });

            if (response.data.message) {
                setSuccess(response.data.message);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Signup failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    if (success) {
        return (
            <div className="login-container">
                <div className="login-box">
                    <h2>Check your email</h2>
                    <p className="subtitle">Your account is created, but not fully verified yet.</p>
                    <div className="success-message">{success}</div>
                    <p>Open the verification link we sent, then sign in.</p>
                    <div className="signup-link">
                        <p><Link to="/login">Continue to login</Link></p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-box">
                <h2>Create Account</h2>
                <p className="subtitle">Join our monitoring system</p>
                
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label htmlFor="commray-signup-tz">Time zone</label>
                    <UserTimezoneSelect showInnerLabel={false} selectId="commray-signup-tz" embedded />
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="username">Username</label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="Enter your username"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="you@company.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="organization">Organization</label>
                        <input
                            type="text"
                            id="organization"
                            name="organization"
                            value={formData.organization}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="Company or team name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="At least 15 characters"
                        />
                        <PasswordHint value={formData.password} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="Confirm your password"
                        />
                    </div>

                    <button type="submit" disabled={loading} className="login-button">
                        {loading ? (
                            <>
                                <div className="loading-spinner"></div>
                                Creating Account...
                            </>
                        ) : (
                            'Create Account'
                        )}
                    </button>
                </form>

                <div className="signup-link">
                    <p>Already have an account? <Link to="/login">Login here</Link></p>
                </div>
            </div>
        </div>
    );
};

export default Signup;
